import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  AlertTitle,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  TextField,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Autocomplete,
  Grid,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowForward as ArrowForwardIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const PREVIEW_ROW_LIMIT = 100;
const FULL_TABLE_ROW_LIMIT = 200;

const STEPS = ['Selecionar planilha', 'Preview e validacao', 'Resultado'];

const ACOES_LABEL = {
  criar: 'Criar',
  trocar: 'Trocar',
  corrigir_ativo: 'Corrigir comercial (mantem DataInicio)',
  corrigir_data_inicio: 'Corrigir DataInicio',
  manter: 'Manter',
  ignorar_geral: 'Ignorada (Geral)',
};

const CHIP_DEFS = [
  { key: 'total_linhas', label: 'Linhas processadas', color: 'default', tip: 'Linhas validas que serao aplicadas (excluindo ignoradas).' },
  { key: 'ignoradas_geral', label: 'Ignoradas (Geral)', color: 'default', tip: 'Linhas com Categoria=Geral - seguradora gerenciada por corretora, nada a fazer.' },
  { key: 'ignoradas_modo_divergente', label: 'Ignoradas (modo divergente)', color: 'default', tip: 'Linhas de seguradoras em "comercial_por=corretora" que NAO foram marcadas para conversao.' },
  { key: 'hist_criado', label: 'Vinculos novos', color: 'success', tip: 'Seguradoras sem historico ativo - sera criado um INSERT em HistoricoResponsaveisSeguradoras.' },
  { key: 'hist_trocado', label: 'Trocas', color: 'warning', tip: 'TROCAS de comercial: fecha o ativo (DataFim = data planilha - 1) e abre um novo registro.' },
  { key: 'hist_corrigido', label: 'Correcoes (ativo)', color: 'warning', tip: 'CORRECAO: UPDATE em IDResponsavel no registro ativo, mantendo a DataInicio. Acontece quando a data da planilha <= DataInicio do ativo.' },
  { key: 'hist_data_corrigida', label: 'Correcoes (DataInicio)', color: 'info', tip: 'Mesmo responsavel, mas a planilha tem uma data MAIS ANTIGA. Atualiza apenas a DataInicio do registro ativo.' },
  { key: 'hist_ja_ok', label: 'Ja OK', color: 'default', tip: 'Vinculos que ja conferem com a planilha (mesmo responsavel e mesma data ou data igual/anterior na planilha).' },
  { key: 'responsavel_criado', label: 'Responsavel novo', color: 'success', tip: 'Responsaveis que serao criados na tabela Responsavel (nome novo, match normalizado).' },
  { key: 'seguradora_convertida', label: 'Seguradora convertida', color: 'info', tip: 'Seguradoras que terao comercial_por trocado de "corretora" para "seguradora".' },
  { key: 'erros', label: 'Erros', color: 'error', tip: 'Linhas com erro de validacao. Se > 0, a gravacao fica BLOQUEADA.' },
];

const fmtData = (v) => {
  if (!v) return '-';
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const dt = new Date(s);
  if (!isNaN(dt.getTime())) return dt.toLocaleDateString('pt-BR');
  return s;
};

const StatChip = ({ chipDef, value }) => (
  <Tooltip title={chipDef.tip} arrow placement="top">
    <Chip
      label={`${chipDef.label}: ${value || 0}`}
      color={value > 0 ? chipDef.color : 'default'}
      variant="outlined"
      size="small"
    />
  </Tooltip>
);

const SeccaoMudanca = ({ titulo, count, color, defaultExpanded, children }) => {
  if (!count) return null;
  return (
    <Accordion defaultExpanded={defaultExpanded} sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip label={count} color={color} size="small" />
          <Typography variant="subtitle2">{titulo}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
};

const TabelaMudancas = ({ rows, colunas, limite = PREVIEW_ROW_LIMIT }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          {colunas.map((c) => (
            <TableCell key={c.key} sx={{ fontWeight: 'bold' }}>{c.label}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.slice(0, limite).map((r, i) => (
          <TableRow key={i}>
            {colunas.map((c) => (
              <TableCell key={c.key}>{c.render ? c.render(r) : r[c.key]}</TableCell>
            ))}
          </TableRow>
        ))}
        {rows.length > limite && (
          <TableRow>
            <TableCell colSpan={colunas.length} sx={{ fontStyle: 'italic', textAlign: 'center', bgcolor: 'warning.lighter' }}>
              Exibindo {limite} de {rows.length}. Para validar todos os registros, baixe o relatorio (.xlsx) no botao no rodape.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const SetaMudanca = ({ antes, depois }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
      {antes ?? '-'}
    </Typography>
    <ArrowForwardIcon fontSize="inherit" color="action" />
    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{depois ?? '-'}</Typography>
  </Stack>
);

const CargaSeguradorasDialog = ({ open, onClose, onSuccess }) => {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [erroApi, setErroApi] = useState('');
  const [filtroLinha, setFiltroLinha] = useState('');
  const [convertSegIds, setConvertSegIds] = useState(new Set());
  // nameMappings: { [nome_norm_planilha]: { mode: 'criar'|'mapear', respId: number|null } }
  const [nameMappings, setNameMappings] = useState({});
  // segMappings: { [nome_norm_planilha]: { mode: 'erro'|'mapear', segId: number|null } }
  const [segMappings, setSegMappings] = useState({});
  const [responsaveis, setResponsaveis] = useState([]);
  const [seguradorasAll, setSeguradorasAll] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get('/responsaveis')
      .then((res) => {
        const raw = res.data;
        const lista = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        setResponsaveis(lista);
      })
      .catch(() => setResponsaveis([]));
    api.get('/seguradoras')
      .then((res) => {
        const raw = res.data;
        const lista = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        setSeguradorasAll(lista);
      })
      .catch(() => setSeguradorasAll([]));
  }, [open]);

  const reset = () => {
    setStep(0);
    setFile(null);
    setPreview(null);
    setResultado(null);
    setErroApi('');
    setFiltroLinha('');
    setConvertSegIds(new Set());
    setNameMappings({});
    setSegMappings({});
    setLoading(false);
  };

  // Envia tanto a escolha 'mapear' (id > 0) quanto 'criar'/'erro' (null = sentinela
  // explicita "sem mapeamento", impede o backend de auto-resolver).
  const buildMappingsPayload = () => {
    const out = {};
    Object.entries(nameMappings).forEach(([norm, sel]) => {
      if (!sel) return;
      if (sel.mode === 'mapear' && sel.respId) {
        out[norm] = sel.respId;
      } else if (sel.mode === 'criar') {
        out[norm] = null;
      }
    });
    return out;
  };

  const buildSegMappingsPayload = () => {
    const out = {};
    Object.entries(segMappings).forEach(([norm, sel]) => {
      if (!sel) return;
      if (sel.mode === 'mapear' && sel.segId) {
        out[norm] = sel.segId;
      } else if (sel.mode === 'erro') {
        out[norm] = null;
      }
    });
    return out;
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const enviar = async (execute) => {
    if (!file) return;
    setLoading(true);
    setErroApi('');
    try {
      const form = new FormData();
      form.append('file', file);
      if (execute && convertSegIds.size > 0) {
        form.append('convert_seguradoras', JSON.stringify([...convertSegIds]));
      }
      const mappings = buildMappingsPayload();
      if (Object.keys(mappings).length > 0) {
        form.append('name_mappings', JSON.stringify(mappings));
      }
      const segMaps = buildSegMappingsPayload();
      if (Object.keys(segMaps).length > 0) {
        form.append('seguradora_mappings', JSON.stringify(segMaps));
      }
      const res = await api.post(
        `/admin/seguradoras/carga?execute=${execute ? 'true' : 'false'}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const data = res.data?.data || res.data;
      if (execute) {
        setResultado(data);
        setStep(2);
        onSuccess?.();
      } else {
        const pendentes = data?.fuzzy_pendentes || [];
        setNameMappings((prev) => {
          const next = {};
          pendentes.forEach((p) => {
            const existente = prev[p.nome_norm];
            if (existente) {
              next[p.nome_norm] = existente;
            } else if (p.mapeado_auto) {
              next[p.nome_norm] = { mode: 'mapear', respId: p.mapeado_auto };
            } else if (p.mapeado_para) {
              next[p.nome_norm] = { mode: 'mapear', respId: p.mapeado_para };
            } else {
              next[p.nome_norm] = { mode: 'criar', respId: null };
            }
          });
          return next;
        });
        const pendentesSeg = data?.fuzzy_pendentes_seguradoras || [];
        setSegMappings((prev) => {
          const next = {};
          pendentesSeg.forEach((p) => {
            const existente = prev[p.nome_norm];
            if (existente) {
              next[p.nome_norm] = existente;
            } else if (p.mapeado_auto) {
              next[p.nome_norm] = { mode: 'mapear', segId: p.mapeado_auto };
            } else if (p.mapeado_para) {
              next[p.nome_norm] = { mode: 'mapear', segId: p.mapeado_para };
            } else {
              next[p.nome_norm] = { mode: 'erro', segId: null };
            }
          });
          return next;
        });
        setPreview(data);
        setStep(1);
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      setErroApi(msg);
    } finally {
      setLoading(false);
    }
  };

  const baixarRelatorio = async () => {
    if (!file) return;
    setLoading(true);
    setErroApi('');
    try {
      const form = new FormData();
      form.append('file', file);
      const mappings = buildMappingsPayload();
      if (Object.keys(mappings).length > 0) {
        form.append('name_mappings', JSON.stringify(mappings));
      }
      const segMaps = buildSegMappingsPayload();
      if (Object.keys(segMaps).length > 0) {
        form.append('seguradora_mappings', JSON.stringify(segMaps));
      }
      const res = await api.post(
        '/admin/seguradoras/carga/relatorio',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' }, responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
      a.download = `relatorio_carga_seguradoras_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      setErroApi(`Falha ao baixar relatorio: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleConvertSeg = (id) => {
    setConvertSegIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleConvertTodas = () => {
    if (!preview) return;
    const todas = preview.seguradoras_modo_divergente || [];
    if (convertSegIds.size === todas.length) {
      setConvertSegIds(new Set());
    } else {
      setConvertSegIds(new Set(todas.map((s) => s.id)));
    }
  };

  const grupos = useMemo(() => {
    const empty = {
      criar: [], trocar: [], corrigir_ativo: [], corrigir_data_inicio: [],
      manter: [], ignorar_geral: [], ignorar_divergente: [],
    };
    if (!preview?.decisoes) return empty;
    const out = { ...empty };
    preview.decisoes.forEach((d) => {
      if (d.erros?.length) return;
      if (d.acao_historico === 'ignorar_geral') {
        out.ignorar_geral.push(d);
        return;
      }
      if (d.seguradora_modo && d.seguradora_modo !== 'seguradora' && !convertSegIds.has(d.seguradora_id)) {
        out.ignorar_divergente.push(d);
        return;
      }
      if (out[d.acao_historico]) out[d.acao_historico].push(d);
    });
    return out;
  }, [preview, convertSegIds]);

  const decisoesFiltradas = useMemo(() => {
    if (!preview) return [];
    if (!filtroLinha) return preview.decisoes;
    const f = filtroLinha.toLowerCase();
    return preview.decisoes.filter((d) =>
      (d.nome_planilha || '').toLowerCase().includes(f) ||
      (d.seguradora_nome_db || '').toLowerCase().includes(f) ||
      (d.comercial_nome || '').toLowerCase().includes(f) ||
      String(d.linha || '').includes(f)
    );
  }, [preview, filtroLinha]);

  const renderStats = (stats, titulo) => {
    if (!stats) return null;
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>{titulo} — passe o mouse em cada metrica</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {CHIP_DEFS.map((cd) => (
            <StatChip key={cd.key} chipDef={cd} value={stats[cd.key]} />
          ))}
        </Stack>
      </Paper>
    );
  };

  const divergentes = preview?.seguradoras_modo_divergente || [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Carga em massa de comerciais por seguradora
        <IconButton onClick={handleClose} disabled={loading}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((s) => (
            <Step key={s}><StepLabel>{s}</StepLabel></Step>
          ))}
        </Stepper>

        {erroApi && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroApi('')}>{erroApi}</Alert>
        )}

        {step === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Envie a planilha do Agendor (.xlsx) com as colunas:
              <strong> Nome, Categoria, Data de cadastro</strong>.
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li><strong>Nome</strong> = nome da seguradora (precisa bater com o cadastro do banco).</li>
                <li><strong>Categoria</strong> = comercial responsavel. Se for "Geral", a linha e ignorada.</li>
                <li><strong>Data de cadastro</strong> = data de atualizacao do comercial (vira DataInicio do historico).</li>
              </ul>
            </Alert>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <Paper
              variant="outlined"
              sx={{
                p: 4, textAlign: 'center', cursor: 'pointer',
                borderStyle: 'dashed', borderWidth: 2,
                bgcolor: file ? 'action.hover' : 'transparent',
              }}
              onClick={() => inputRef.current?.click()}
            >
              <UploadIcon sx={{ fontSize: 48, color: 'action.active', mb: 1 }} />
              <Typography variant="body1">
                {file ? file.name : 'Clique para selecionar o arquivo .xlsx'}
              </Typography>
              {file && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {(file.size / 1024).toFixed(1)} KB
                </Typography>
              )}
            </Paper>
          </Box>
        )}

        {step === 1 && preview && (
          <Box>
            {preview.erros?.length > 0 ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>{preview.erros.length} erro(s) encontrados</AlertTitle>
                Corrija a planilha (ou cadastre as seguradoras faltantes no banco) e envie novamente. A gravacao esta bloqueada.
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 2 }}>
                <AlertTitle>Validacao OK</AlertTitle>
                Planilha sem erros. Revise as mudancas abaixo e confirme para gravar.
              </Alert>
            )}

            {renderStats(preview.stats, 'Resumo')}

            {divergentes.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'warning.lighter' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" color="warning.dark">
                    {divergentes.length} seguradora(s) em modo "comercial_por=corretora"
                  </Typography>
                  <Button size="small" onClick={toggleConvertTodas}>
                    {convertSegIds.size === divergentes.length ? 'Desmarcar todas' : 'Marcar todas'}
                  </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Marque quais devem ser convertidas para <strong>comercial_por=seguradora</strong> antes da carga.
                  Seguradoras nao marcadas terao suas linhas IGNORADAS (o historico por seguradora so vale no modo "seguradora").
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {divergentes.map((s) => (
                    <FormControlLabel
                      key={s.id}
                      control={
                        <Checkbox
                          checked={convertSegIds.has(s.id)}
                          onChange={() => toggleConvertSeg(s.id)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          <strong>{s.nome}</strong>
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (ID {s.id} - modo atual: {s.comercial_por})
                          </Typography>
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {preview.fuzzy_pendentes_seguradoras?.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'info.lighter' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" color="info.dark">
                    {preview.fuzzy_pendentes_seguradoras.length} seguradora(s) sem match exato — confirme o que fazer
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => enviar(false)}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
                  >
                    Re-validar com mapeamentos
                  </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  A planilha trouxe nomes de seguradora que nao bateram exatamente com o banco.
                  Para cada um, escolha: <strong>mapear</strong> para a seguradora existente correspondente
                  (ex: "Justos" da planilha = "Justos Seguros" do banco) ou <strong>manter como erro</strong>
                  (a linha ficara bloqueada). Apos ajustar, clique em <strong>Re-validar</strong>.
                </Typography>
                <Stack spacing={1.5}>
                  {preview.fuzzy_pendentes_seguradoras.map((p) => {
                    const sel = segMappings[p.nome_norm] || { mode: 'erro', segId: null };
                    const selectedSeg = seguradorasAll.find((s) => s.id === sel.segId) || null;
                    return (
                      <Paper key={p.nome_norm} variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={12} md={3}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="body2">
                                Planilha: <strong>"{p.nome_planilha}"</strong>
                              </Typography>
                              {p.mapeado_auto && (
                                <Chip label="auto" size="small" color="success" variant="outlined"
                                      title="Mapeado automaticamente (1 sugestao unica)" />
                              )}
                            </Stack>
                            {p.candidatos.length > 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                {p.candidatos.length} sugestao(oes) encontrada(s)
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Nenhuma sugestao automatica - escolha manualmente
                              </Typography>
                            )}
                          </Grid>
                          <Grid item xs={12} md={9}>
                            <RadioGroup
                              row
                              value={sel.mode}
                              onChange={(e) => {
                                const mode = e.target.value;
                                setSegMappings((prev) => ({
                                  ...prev,
                                  [p.nome_norm]: mode === 'mapear'
                                    ? { mode, segId: sel.segId || p.candidatos[0]?.id || null }
                                    : { mode: 'erro', segId: null },
                                }));
                              }}
                            >
                              <FormControlLabel
                                value="mapear"
                                control={<Radio size="small" />}
                                label={<Typography variant="body2">Mapear para existente</Typography>}
                              />
                              <FormControlLabel
                                value="erro"
                                control={<Radio size="small" />}
                                label={<Typography variant="body2">Manter como erro</Typography>}
                              />
                            </RadioGroup>
                            {sel.mode === 'mapear' && (
                              <Autocomplete
                                size="small"
                                options={seguradorasAll}
                                getOptionLabel={(o) => o.nome || ''}
                                value={selectedSeg}
                                onChange={(_, v) => setSegMappings((prev) => ({
                                  ...prev,
                                  [p.nome_norm]: { mode: 'mapear', segId: v ? v.id : null },
                                }))}
                                groupBy={(o) => (p.candidatos.some((c) => c.id === o.id) ? 'Sugestoes' : 'Todas')}
                                renderInput={(params) => (
                                  <TextField {...params} label="Seguradora existente" required />
                                )}
                                ListboxProps={{ style: { maxHeight: 240 } }}
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}
                </Stack>
              </Paper>
            )}

            {preview.fuzzy_pendentes?.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'info.lighter' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" color="info.dark">
                    {preview.fuzzy_pendentes.length} comercial(is) sem match exato — confirme o que fazer
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => enviar(false)}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
                  >
                    Re-validar com mapeamentos
                  </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  A planilha trouxe nomes que nao bateram exatamente com a tabela Responsavel.
                  Para cada um, escolha: <strong>mapear</strong> para um responsavel existente
                  (recomendado quando "Camila" da planilha = "Camila Aparecida..." do banco) ou
                  <strong> criar novo</strong>. Apos ajustar, clique em <strong>Re-validar</strong> para
                  ver o efeito real nas decisoes abaixo.
                </Typography>
                <Stack spacing={1.5}>
                  {preview.fuzzy_pendentes.map((p) => {
                    const sel = nameMappings[p.nome_norm] || { mode: 'criar', respId: null };
                    const selectedResp = responsaveis.find((r) => r.id === sel.respId) || null;
                    return (
                      <Paper key={p.nome_norm} variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={12} md={3}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="body2">
                                Planilha: <strong>"{p.nome_planilha}"</strong>
                              </Typography>
                              {p.mapeado_auto && (
                                <Chip label="auto" size="small" color="success" variant="outlined"
                                      title="Mapeado automaticamente (1 sugestao unica)" />
                              )}
                            </Stack>
                            {p.candidatos.length > 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                {p.candidatos.length} sugestao(oes) encontrada(s)
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Nenhuma sugestao automatica
                              </Typography>
                            )}
                          </Grid>
                          <Grid item xs={12} md={9}>
                            <RadioGroup
                              row
                              value={sel.mode}
                              onChange={(e) => {
                                const mode = e.target.value;
                                setNameMappings((prev) => ({
                                  ...prev,
                                  [p.nome_norm]: mode === 'mapear'
                                    ? { mode, respId: sel.respId || p.candidatos[0]?.id || null }
                                    : { mode: 'criar', respId: null },
                                }));
                              }}
                            >
                              <FormControlLabel
                                value="mapear"
                                control={<Radio size="small" />}
                                label={<Typography variant="body2">Usar existente</Typography>}
                              />
                              <FormControlLabel
                                value="criar"
                                control={<Radio size="small" />}
                                label={<Typography variant="body2">Criar novo</Typography>}
                              />
                            </RadioGroup>
                            {sel.mode === 'mapear' && (
                              <Autocomplete
                                size="small"
                                options={responsaveis}
                                getOptionLabel={(o) => o.Responsavel || o.nome || ''}
                                value={selectedResp}
                                onChange={(_, v) => setNameMappings((prev) => ({
                                  ...prev,
                                  [p.nome_norm]: { mode: 'mapear', respId: v ? v.id : null },
                                }))}
                                groupBy={(o) => (p.candidatos.some((c) => c.id === o.id) ? 'Sugestoes' : 'Todos')}
                                renderInput={(params) => (
                                  <TextField {...params} label="Responsavel existente" required />
                                )}
                                ListboxProps={{ style: { maxHeight: 240 } }}
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}
                </Stack>
              </Paper>
            )}

            {(() => {
              // "Novos responsaveis" efetivos = aqueles que ficaram em "Criar novo"
              // OU que foram pra "Usar existente" sem nenhum respId selecionado.
              // Quando houver fuzzy_pendentes, derivamos a partir das escolhas locais
              // (o backend so sabe das escolhas que ja foram enviadas).
              const fuzzy = preview.fuzzy_pendentes || [];
              const novos = fuzzy.length > 0
                ? fuzzy.filter((p) => {
                    const sel = nameMappings[p.nome_norm];
                    return !sel || sel.mode === 'criar' || !sel.respId;
                  }).map((p) => ({ nome: p.nome_planilha }))
                : (preview.novos_responsaveis || []);

              if (novos.length === 0) return null;
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>{novos.length} responsavel(is) sera(o) criado(s) na tabela Responsavel se voce confirmar agora</AlertTitle>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {novos.map((r, i) => (
                      <Chip key={i} label={r.nome} size="small" color="warning" />
                    ))}
                  </Stack>
                  {fuzzy.length > 0 && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                      Esta contagem reflete as escolhas atuais nos radios acima. Mude para "Usar existente"
                      o que voce nao quer criar.
                    </Typography>
                  )}
                </Alert>
              );
            })()}

            {preview.warnings?.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom color="warning.main">
                  Avisos ({preview.warnings.length})
                </Typography>
                <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                  {preview.warnings.slice(0, 50).map((w, i) => (
                    <Typography key={i} variant="body2">
                      Linha {w.linha}: {w.mensagem}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            )}

            {preview.erros?.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom color="error">
                  Erros ({preview.erros.length})
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {preview.erros.slice(0, 100).map((e, i) => (
                    <Typography key={i} variant="body2" color="error">
                      {e.tipo === 'cabecalho'
                        ? e.mensagem
                        : `Linha ${e.linha} (${e.nome ?? '?'}): ${(e.erros || []).join('; ')}`}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            )}

            <Divider sx={{ my: 2 }}>
              <Chip label="O QUE VAI MUDAR NO BANCO" size="small" />
            </Divider>

            <SeccaoMudanca
              titulo="Vinculos novos (INSERT em HistoricoResponsaveisSeguradoras)"
              count={grupos.criar.length}
              color="success"
              defaultExpanded
            >
              <TabelaMudancas
                rows={grupos.criar}
                colunas={[
                  { key: 'linha', label: 'Linha' },
                  { key: 'seguradora_nome_db', label: 'Seguradora' },
                  { key: 'comercial_nome', label: 'Comercial' },
                  { key: 'data_atualizacao', label: 'DataInicio', render: (r) => fmtData(r.data_atualizacao) },
                ]}
              />
            </SeccaoMudanca>

            <SeccaoMudanca
              titulo="Trocas de comercial (fecha ativo + abre novo)"
              count={grupos.trocar.length}
              color="warning"
              defaultExpanded
            >
              <TabelaMudancas
                rows={grupos.trocar}
                colunas={[
                  { key: 'linha', label: 'Linha' },
                  { key: 'seguradora_nome_db', label: 'Seguradora' },
                  {
                    key: 'troca',
                    label: 'Comercial (antes -> depois)',
                    render: (r) => (
                      <SetaMudanca
                        antes={r.responsavel_anterior_nome || `id=${r.responsavel_anterior_id}`}
                        depois={r.comercial_nome}
                      />
                    ),
                  },
                  {
                    key: 'datas',
                    label: 'Vigencia ativo: inicio -> novo fim',
                    render: (r) => (
                      <SetaMudanca
                        antes={`${fmtData(r.data_inicio_ativo_anterior)} - aberto`}
                        depois={`${fmtData(r.data_inicio_ativo_anterior)} - ${fmtData(r.data_fim_calculada)}`}
                      />
                    ),
                  },
                  {
                    key: 'novo',
                    label: 'Novo registro',
                    render: (r) => (
                      <Typography variant="body2">
                        {fmtData(r.data_atualizacao)} - aberto
                      </Typography>
                    ),
                  },
                ]}
              />
            </SeccaoMudanca>

            <SeccaoMudanca
              titulo="Correcoes no ativo (UPDATE em IDResponsavel, mantem DataInicio)"
              count={grupos.corrigir_ativo.length}
              color="warning"
              defaultExpanded
            >
              <TabelaMudancas
                rows={grupos.corrigir_ativo}
                colunas={[
                  { key: 'linha', label: 'Linha' },
                  { key: 'seguradora_nome_db', label: 'Seguradora' },
                  {
                    key: 'troca',
                    label: 'Comercial (antes -> depois)',
                    render: (r) => (
                      <SetaMudanca
                        antes={r.responsavel_anterior_nome || `id=${r.responsavel_anterior_id}`}
                        depois={r.comercial_nome}
                      />
                    ),
                  },
                  {
                    key: 'datainicio',
                    label: 'DataInicio mantida',
                    render: (r) => fmtData(r.data_inicio_ativo_anterior),
                  },
                ]}
              />
            </SeccaoMudanca>

            <SeccaoMudanca
              titulo="Correcoes de DataInicio (mesmo responsavel, planilha mais antiga)"
              count={grupos.corrigir_data_inicio.length}
              color="info"
              defaultExpanded
            >
              <TabelaMudancas
                rows={grupos.corrigir_data_inicio}
                colunas={[
                  { key: 'linha', label: 'Linha' },
                  { key: 'seguradora_nome_db', label: 'Seguradora' },
                  { key: 'comercial_nome', label: 'Comercial (mantido)' },
                  {
                    key: 'datas',
                    label: 'DataInicio (antes -> depois)',
                    render: (r) => (
                      <SetaMudanca
                        antes={fmtData(r.data_inicio_ativo_anterior)}
                        depois={fmtData(r.data_atualizacao)}
                      />
                    ),
                  },
                ]}
              />
            </SeccaoMudanca>

            <SeccaoMudanca
              titulo="Sem alteracoes"
              count={grupos.manter.length}
              color="default"
              defaultExpanded={false}
            >
              <TabelaMudancas
                rows={grupos.manter}
                colunas={[
                  { key: 'linha', label: 'Linha' },
                  { key: 'seguradora_nome_db', label: 'Seguradora' },
                  { key: 'comercial_nome', label: 'Comercial atual' },
                ]}
              />
            </SeccaoMudanca>

            <SeccaoMudanca
              titulo="Ignoradas (Categoria=Geral)"
              count={grupos.ignorar_geral.length}
              color="default"
              defaultExpanded={false}
            >
              <TabelaMudancas
                rows={grupos.ignorar_geral}
                colunas={[
                  { key: 'linha', label: 'Linha' },
                  { key: 'nome_planilha', label: 'Seguradora (planilha)' },
                  { key: 'seguradora_nome_db', label: 'Seguradora (DB)' },
                ]}
              />
            </SeccaoMudanca>

            <SeccaoMudanca
              titulo="Ignoradas (modo divergente nao convertido)"
              count={grupos.ignorar_divergente.length}
              color="default"
              defaultExpanded={false}
            >
              <TabelaMudancas
                rows={grupos.ignorar_divergente}
                colunas={[
                  { key: 'linha', label: 'Linha' },
                  { key: 'seguradora_nome_db', label: 'Seguradora' },
                  { key: 'seguradora_modo', label: 'Modo atual' },
                  { key: 'comercial_nome', label: 'Comercial (planilha)' },
                ]}
              />
            </SeccaoMudanca>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
              <Typography variant="subtitle2">
                Tabela completa por linha ({decisoesFiltradas.length}/{preview.decisoes.length})
              </Typography>
              <TextField
                size="small"
                placeholder="Filtrar por seguradora, comercial ou linha"
                value={filtroLinha}
                onChange={(e) => setFiltroLinha(e.target.value)}
                sx={{ flexGrow: 1, maxWidth: 400 }}
              />
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Linha</TableCell>
                    <TableCell>Seguradora (planilha)</TableCell>
                    <TableCell>Seguradora (DB)</TableCell>
                    <TableCell>Modo</TableCell>
                    <TableCell>Comercial</TableCell>
                    <TableCell>Acao</TableCell>
                    <TableCell>Erros</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {decisoesFiltradas.slice(0, FULL_TABLE_ROW_LIMIT).map((d, i) => (
                    <TableRow key={i}>
                      <TableCell>{d.linha}</TableCell>
                      <TableCell>{d.nome_planilha}</TableCell>
                      <TableCell>{d.seguradora_nome_db || '-'}</TableCell>
                      <TableCell>
                        {d.seguradora_modo && (
                          <Chip
                            label={d.seguradora_modo}
                            size="small"
                            color={d.seguradora_modo === 'seguradora' ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {d.comercial_nome}
                        {d.responsavel_novo && <Chip label="novo" size="small" color="warning" sx={{ ml: 1 }} />}
                      </TableCell>
                      <TableCell>{ACOES_LABEL[d.acao_historico] || d.acao_historico || '-'}</TableCell>
                      <TableCell>
                        {d.erros?.length > 0 && (
                          <Tooltip title={d.erros.join('; ')}>
                            <Chip label={`${d.erros.length} erro(s)`} size="small" color="error" />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {decisoesFiltradas.length > FULL_TABLE_ROW_LIMIT && (
              <Alert severity="info" sx={{ mt: 1 }} icon={<DownloadIcon />}>
                Exibindo {FULL_TABLE_ROW_LIMIT} de {decisoesFiltradas.length} linhas. Use o filtro acima ou
                <strong> baixe o relatorio completo (.xlsx)</strong> no botao no rodape para validar todos os registros offline.
              </Alert>
            )}
          </Box>
        )}

        {step === 2 && resultado && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>Carga concluida</AlertTitle>
              {resultado.stats.hist_criado} vinculo(s) novo(s),
              {' '}{resultado.stats.hist_trocado} troca(s),
              {' '}{resultado.stats.hist_corrigido + resultado.stats.hist_data_corrigida} correcao(oes),
              {' '}{resultado.stats.seguradora_convertida} seguradora(s) convertida(s),
              {' '}{resultado.stats.responsavel_criado} responsavel(is) novo(s).
              {' '}<strong>{resultado.resultados_bi_atualizados}</strong> registro(s) do Resultados_BI atualizado(s).
            </Alert>
            {renderStats(resultado.stats, 'Resumo final')}
            {resultado.seguradoras_convertidas?.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <AlertTitle>Seguradoras convertidas para comercial_por=seguradora</AlertTitle>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {resultado.seguradoras_convertidas.map((s) => (
                    <Chip key={s.id} label={s.nome} size="small" color="info" />
                  ))}
                </Stack>
              </Alert>
            )}
            <Alert severity="info" sx={{ mt: 2 }}>
              Se quiser arquivar o detalhe do que foi gravado, baixe o relatorio em .xlsx no botao do rodape. Nada e salvo no servidor.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 0 && (
          <>
            <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={() => enviar(false)}
              disabled={!file || loading}
              startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
            >
              Validar planilha
            </Button>
          </>
        )}
        {step === 1 && (
          <>
            <Button onClick={() => { setStep(0); setPreview(null); setConvertSegIds(new Set()); setNameMappings({}); setSegMappings({}); }} disabled={loading}>Voltar</Button>
            <Button
              variant="outlined"
              onClick={baixarRelatorio}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} /> : <DownloadIcon />}
            >
              Baixar relatorio (.xlsx)
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => enviar(true)}
              disabled={loading || (preview?.erros?.length > 0)}
              startIcon={loading ? <CircularProgress size={18} /> : null}
            >
              Confirmar e gravar
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            <Button
              variant="outlined"
              onClick={baixarRelatorio}
              disabled={loading || !file}
              startIcon={loading ? <CircularProgress size={18} /> : <DownloadIcon />}
            >
              Baixar relatorio (.xlsx)
            </Button>
            <Button variant="contained" onClick={handleClose}>Fechar</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CargaSeguradorasDialog;
